import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styles from './Stock.module.css';
import StateAvailabilityPanel from './StateAvailabilityPanel';
import AdminMapChart from './AdminMapChart';
import { stateZipCodes } from '../utils/StateZipCodes';

const placeholderImage = '/images/phone-track.webp';

const STATE_NAME_BY_CODE = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming'
};

const normalizeDateInputValue = (value) => {
    if (!value) {
        return '';
    }

    let date;
    if (typeof value === 'number') {
        const millis = value < 1_000_000_000_000 ? value * 1000 : value;
        date = new Date(millis);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (typeof value.epochSecond === 'number') {
            const millis = value.epochSecond * 1000 + Math.floor((value.nano || 0) / 1_000_000);
            date = new Date(millis);
        } else {
            date = new Date(value);
        }
    } else if (Array.isArray(value) && value.length >= 3) {
        const [year, month, day] = value;
        date = new Date(Date.UTC(year, month - 1, day));
    } else {
        date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toISOString().slice(0, 10);
};

const renderAvailabilityPill = (label, isAvailable) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.35,
            borderRadius: 999,
            bgcolor: isAvailable ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
            color: isAvailable ? 'var(--sm-success-strong)' : 'var(--sm-danger-strong)'
        }}
    >
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)'
            }}
        />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1 }}>
            {label}
        </Typography>
    </Box>
);

const RETAILER_OFFER_OPTIONS = [
    {
        value: 'NEWEGG',
        label: 'Newegg',
        helperText: "N82E number from the product's own listing — 9SIA numbers are marketplace resellers",
        invalidText: 'Newegg item numbers look like N82E16 followed by 9 digits (e.g. N82E16814137891).',
        pattern: /^N82E16\d{9}$/
    },
    {
        value: 'WALMART',
        label: 'Walmart',
        helperText: 'the number from the walmart.com/ip/ URL',
        invalidText: 'Walmart item ids are the 6-12 digit number from the walmart.com/ip/ URL.',
        pattern: /^\d{6,12}$/
    },
    {
        value: 'AMAZON',
        label: 'Amazon',
        helperText: 'the 10-character child ASIN',
        invalidText: 'Amazon ASINs are exactly 10 letters and digits (e.g. B0C7WWXYZ1).',
        pattern: /^[A-Z0-9]{10}$/
    }
];

// Mirrors RetailerOfferAdminService.normalizeItemNumber so admins get the
// same message locally that the server would send back.
const validateRetailerItemNumber = (retailer, rawValue) => {
    const option = RETAILER_OFFER_OPTIONS.find((candidate) => candidate.value === retailer);
    if (!option) {
        return { error: 'Retailer must be one of NEWEGG, WALMART, AMAZON.' };
    }

    let value = (rawValue || '').trim();
    if (retailer === 'AMAZON') {
        value = value.toUpperCase();
    }
    if (retailer === 'NEWEGG' && value.toUpperCase().startsWith('9SIA')) {
        return {
            error: '9SIA numbers are Newegg marketplace listings sold by third-party resellers. '
                + "Use the N82E16 item number from Newegg's own listing."
        };
    }
    if (!option.pattern.test(value)) {
        return { error: option.invalidText };
    }
    return { value };
};

const renderRetailerOfferVerification = (offer) => {
    if (offer?.firstPartyVerified) {
        return (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'var(--sm-success-strong)' }}>
                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'var(--sm-success-bright)' }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>
                    Verified
                </Typography>
            </Stack>
        );
    }
    if (offer?.lastRejectReason) {
        return (
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--sm-danger-strong)' }}>
                rejected: {offer.lastRejectReason}
            </Typography>
        );
    }
    return (
        <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
            —
        </Typography>
    );
};

const extractMcProductIdFromUrl = (urlValue) => {
    if (!urlValue || typeof urlValue !== 'string') {
        return '';
    }

    const match = urlValue.match(/\/product\/(\d+)/i);
    return match?.[1] || '';
};

const AdminStockEditor = ({
    form,
    mode,
    loading,
    selectedSku,
    onChange,
    onSave,
    onDelete,
    stateData,
    onToggleState,
    mcOfferForm,
    hasMcOffer,
    mcOfferExpanded,
    onToggleMcOfferSection,
    onMcOfferChange,
    onImportMcOfferFromUrl,
    onDeleteMcOffer,
    mcStateData,
    onToggleMcState,
    selectedStockId,
    retailerOffers,
    onAddRetailerOffer,
    onDeleteRetailerOffer
}) => {
    const [mapRetailer, setMapRetailer] = useState('combined');
    const [retailerOffersExpanded, setRetailerOffersExpanded] = useState(false);
    const [retailerOfferDraft, setRetailerOfferDraft] = useState({
        retailer: 'NEWEGG',
        externalItemNumber: '',
        url: ''
    });
    const [retailerOfferError, setRetailerOfferError] = useState('');

    const offers = useMemo(() => (Array.isArray(retailerOffers) ? retailerOffers : []), [retailerOffers]);

    useEffect(() => {
        setRetailerOffersExpanded(false);
        setRetailerOfferDraft({ retailer: 'NEWEGG', externalItemNumber: '', url: '' });
        setRetailerOfferError('');
    }, [selectedStockId]);

    useEffect(() => {
        if (offers.length > 0) {
            setRetailerOffersExpanded(true);
        }
    }, [offers.length]);

    useEffect(() => {
        if (mapRetailer === 'microcenter' && !hasMcOffer && !mcOfferExpanded) {
            setMapRetailer('combined');
        }
    }, [hasMcOffer, mapRetailer, mcOfferExpanded]);

    const stateCodeByName = useMemo(() => {
        return Object.entries(STATE_NAME_BY_CODE).reduce((acc, [stateCode, stateName]) => {
            acc[stateName.toLowerCase()] = stateCode;
            return acc;
        }, {});
    }, []);

    const bestBuyStateData = useMemo(() => {
        return (Array.isArray(stateData) ? stateData : [])
            .map((state) => {
                const normalizedCode = String(state?.stateCode || '').trim().toUpperCase();
                const stateName = state?.stateName || STATE_NAME_BY_CODE[normalizedCode] || '';
                if (!stateName) {
                    return null;
                }

                return {
                    stateName,
                    stateCode: normalizedCode,
                    available: !!state?.available,
                    hasBestBuy: !!state?.available,
                    hasMicroCenter: false,
                    zipCode: state?.zipCode || stateZipCodes[stateName] || ''
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.stateName.localeCompare(b.stateName));
    }, [stateData]);

    const microcenterStateData = useMemo(() => {
        return (Array.isArray(mcStateData) ? mcStateData : [])
            .map((state) => {
                const normalizedCode = String(state?.stateCode || '').trim().toUpperCase();
                const stateName = normalizedCode
                    ? STATE_NAME_BY_CODE[normalizedCode] || ''
                    : String(state?.stateName || '').trim();

                if (!stateName) {
                    return null;
                }

                const resolvedStateCode = normalizedCode || stateCodeByName[stateName.toLowerCase()] || '';

                return {
                    stateName,
                    stateCode: resolvedStateCode,
                    available: !!state?.available,
                    hasBestBuy: false,
                    hasMicroCenter: !!state?.available,
                    zipCode: state?.zipCode || stateZipCodes[stateName] || ''
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.stateName.localeCompare(b.stateName));
    }, [mcStateData, stateCodeByName]);

    const mergedStateData = useMemo(() => {
        const byState = new Map();

        bestBuyStateData.forEach((state) => {
            byState.set(state.stateName.toLowerCase(), {
                ...state
            });
        });

        microcenterStateData.forEach((state) => {
            const key = state.stateName.toLowerCase();
            const existing = byState.get(key) || {
                stateName: state.stateName,
                stateCode: state.stateCode,
                available: false,
                hasBestBuy: false,
                hasMicroCenter: false,
                zipCode: state.zipCode
            };

            byState.set(key, {
                ...existing,
                available: existing.available || state.available,
                hasMicroCenter: existing.hasMicroCenter || state.available,
                stateCode: existing.stateCode || state.stateCode,
                zipCode: existing.zipCode || state.zipCode || stateZipCodes[state.stateName] || ''
            });
        });

        return Array.from(byState.values()).sort((a, b) => a.stateName.localeCompare(b.stateName));
    }, [bestBuyStateData, microcenterStateData]);

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        onChange({ ...form, [field]: value });
    };

    const handleMcOfferChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        const nextForm = { ...mcOfferForm, [field]: value };
        if (field === 'url') {
            const extractedProductId = extractMcProductIdFromUrl(value);
            if (extractedProductId) {
                nextForm.mcProductId = extractedProductId;
            }
        }
        onMcOfferChange(nextForm);
    };

    const handleRetailerOfferDraftChange = (field) => (event) => {
        const value = event.target.value;
        setRetailerOfferDraft((previous) => ({ ...previous, [field]: value }));
        if (field !== 'url') {
            setRetailerOfferError('');
        }
    };

    const handleAddRetailerOffer = async () => {
        const validation = validateRetailerItemNumber(
            retailerOfferDraft.retailer,
            retailerOfferDraft.externalItemNumber
        );
        if (validation.error) {
            setRetailerOfferError(validation.error);
            return;
        }

        setRetailerOfferError('');
        const succeeded = await onAddRetailerOffer({
            retailer: retailerOfferDraft.retailer,
            externalItemNumber: validation.value,
            url: retailerOfferDraft.url?.trim() || null
        });
        if (succeeded) {
            setRetailerOfferDraft((previous) => ({ ...previous, externalItemNumber: '', url: '' }));
        }
    };

    const selectedRetailerOption = RETAILER_OFFER_OPTIONS.find(
        (candidate) => candidate.value === retailerOfferDraft.retailer
    );

    const previewUrl = form.sku ? `/skus/${form.sku}` : null;
    const mapData = mapRetailer === 'microcenter'
        ? microcenterStateData
        : mapRetailer === 'bestbuy'
            ? bestBuyStateData
            : mergedStateData;
    const handleMapToggle = mapRetailer === 'microcenter'
        ? onToggleMcState
        : mapRetailer === 'bestbuy'
            ? onToggleState
            : undefined;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {mode === 'create' ? 'Create Stock' : 'Edit Stock'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Mirrors the SKU page layout. State map is editable here.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {previewUrl && (
                        <Button component="a" href={previewUrl} target="_blank" rel="noreferrer" variant="outlined">
                            View
                        </Button>
                    )}
                    {mode === 'edit' && (
                        <Button variant="outlined" color="error" onClick={onDelete} disabled={loading}>
                            Delete Stock
                        </Button>
                    )}
                    <Button variant="contained" onClick={onSave} disabled={loading}>
                        {mode === 'create' ? 'Create Stock' : 'Save Changes'}
                    </Button>
                </Stack>
            </Box>

            <div className={styles.container} style={{ height: 'auto' }}>
                <div className={styles.container_left}>
                    <div className={styles.overview_panel}>
                        <img src={form.image || placeholderImage} alt={form.name || 'Stock'} />

                        <TextField
                            label="Name"
                            value={form.name || ''}
                            onChange={handleChange('name')}
                            variant="standard"
                            fullWidth
                            sx={{ mt: 2 }}
                        />
                        <TextField
                            label="SKU"
                            value={form.sku || ''}
                            onChange={handleChange('sku')}
                            variant="standard"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={mode === 'edit'}
                        />
                        <TextField
                            label="Category"
                            value={form.productCategory || 'GPU'}
                            onChange={handleChange('productCategory')}
                            variant="standard"
                            fullWidth
                            select
                            sx={{ mt: 2 }}
                        >
                            <MenuItem value="GPU">GPU</MenuItem>
                            <MenuItem value="RAM">RAM</MenuItem>
                            <MenuItem value="SSD">SSD</MenuItem>
                            <MenuItem value="CPU">CPU</MenuItem>
                            <MenuItem value="MAC">MAC</MenuItem>
                            <MenuItem value="CONSOLE">CONSOLE</MenuItem>
                            <MenuItem value="TCG">TCG</MenuItem>
                        </TextField>
                        <TextField
                            label="Manufacturer / Brand"
                            value={form.manufacturer || ''}
                            onChange={handleChange('manufacturer')}
                            variant="standard"
                            fullWidth
                            sx={{ mt: 2 }}
                        />
                    </div>

                    <StateAvailabilityPanel stateData={mergedStateData} />
                </div>

                <div className={styles.container_right}>
                    <div className={styles.details_panel_plain}>
                        <div
                            className={`${styles.offer_sections} ${styles.offer_sections_flush} ${(hasMcOffer || mcOfferExpanded) ? styles.offer_sections_two_col : ''}`}
                        >
                            <div className={styles.offer_section}>
                                <div className={styles.offer_section_header}>
                                    <div className={styles.offer_section_brand}>
                                        <img
                                            src="/images/pics/Best_Buy_Logo.svg.png"
                                            alt="Best Buy"
                                            className={styles.offer_section_logo}
                                        />
                                        <span className={styles.offer_section_title}>Best Buy</span>
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Price</div>
                                    <div className={styles.offer_value}>
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                value={form.regularPrice ?? ''}
                                                onChange={handleChange('regularPrice')}
                                                placeholder="Regular"
                                                variant="standard"
                                                inputProps={{ inputMode: 'decimal' }}
                                                sx={{ width: 90 }}
                                            />
                                            <TextField
                                                value={form.salePrice ?? ''}
                                                onChange={handleChange('salePrice')}
                                                placeholder="Sale"
                                                variant="standard"
                                                inputProps={{ inputMode: 'decimal' }}
                                                sx={{ width: 90 }}
                                            />
                                        </Stack>
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>In-store availability</div>
                                    <div className={styles.offer_value}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {renderAvailabilityPill(
                                                form.inStoreAvailability ? 'Available' : 'Unavailable',
                                                !!form.inStoreAvailability
                                            )}
                                            <Switch
                                                checked={!!form.inStoreAvailability}
                                                onChange={handleChange('inStoreAvailability')}
                                            />
                                        </Stack>
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Online availability</div>
                                    <div className={styles.offer_value}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {renderAvailabilityPill(
                                                form.onlineAvailability ? 'Available' : 'Unavailable',
                                                !!form.onlineAvailability
                                            )}
                                            <Switch
                                                checked={!!form.onlineAvailability}
                                                onChange={handleChange('onlineAvailability')}
                                            />
                                        </Stack>
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Updated in store</div>
                                    <div className={styles.offer_value}>
                                        <TextField
                                            type="date"
                                            value={normalizeDateInputValue(form.inStoreAvailabilityUpdateDate)}
                                            onChange={handleChange('inStoreAvailabilityUpdateDate')}
                                            variant="standard"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Updated online</div>
                                    <div className={styles.offer_value}>
                                        <TextField
                                            type="date"
                                            value={normalizeDateInputValue(form.onlineAvailabilityUpdateDate)}
                                            onChange={handleChange('onlineAvailabilityUpdateDate')}
                                            variant="standard"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Link</div>
                                    <div className={styles.offer_value}>
                                        <TextField
                                            value={form.url || ''}
                                            onChange={handleChange('url')}
                                            variant="standard"
                                            placeholder="https://www.bestbuy.com/..."
                                            sx={{ minWidth: 180 }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {(hasMcOffer || mcOfferExpanded) && (
                                <div className={styles.offer_section}>
                                    <div className={styles.offer_section_header}>
                                        <div className={styles.offer_section_brand}>
                                            <img
                                                src="/images/pics/microcenter-logo.jpeg"
                                                alt="Micro Center"
                                                className={styles.offer_section_logo}
                                            />
                                            <span className={styles.offer_section_title}>Micro Center</span>
                                        </div>
                                        <IconButton
                                            aria-label={hasMcOffer ? 'Remove Micro Center offer' : 'Close Micro Center offer'}
                                            onClick={hasMcOffer ? onDeleteMcOffer : () => onToggleMcOfferSection(false)}
                                            disabled={loading}
                                            size="small"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Price</div>
                                        <div className={styles.offer_value}>
                                            <Stack direction="row" spacing={1}>
                                                <TextField
                                                    value={mcOfferForm?.regularPrice ?? ''}
                                                    onChange={handleMcOfferChange('regularPrice')}
                                                    variant="standard"
                                                    placeholder="Regular"
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    sx={{ width: 90 }}
                                                />
                                                <TextField
                                                    value={mcOfferForm?.salePrice ?? ''}
                                                    onChange={handleMcOfferChange('salePrice')}
                                                    variant="standard"
                                                    placeholder="Sale"
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    sx={{ width: 90 }}
                                                />
                                            </Stack>
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>In-store availability</div>
                                        <div className={styles.offer_value}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {renderAvailabilityPill(
                                                    mcOfferForm?.inStoreAvailability ? 'Available' : 'Unavailable',
                                                    !!mcOfferForm?.inStoreAvailability
                                                )}
                                                <Switch
                                                    checked={!!mcOfferForm?.inStoreAvailability}
                                                    onChange={handleMcOfferChange('inStoreAvailability')}
                                                />
                                            </Stack>
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Updated in store</div>
                                        <div className={styles.offer_value}>
                                            <TextField
                                                type="date"
                                                value={normalizeDateInputValue(mcOfferForm?.inStoreAvailabilityUpdateDate)}
                                                onChange={handleMcOfferChange('inStoreAvailabilityUpdateDate')}
                                                variant="standard"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Link</div>
                                        <div className={styles.offer_value}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <TextField
                                                    value={mcOfferForm?.url || ''}
                                                    onChange={handleMcOfferChange('url')}
                                                    variant="standard"
                                                    placeholder="https://www.microcenter.com/product/..."
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={onImportMcOfferFromUrl}
                                                    disabled={loading}
                                                >
                                                    Import URL
                                                </Button>
                                            </Stack>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>

                    {selectedSku && !hasMcOffer && !mcOfferExpanded && (
                        <div className={styles.details_panel}>
                            <div className={styles.details_panel_row}>
                                <div className={styles.details_panel_label}>Micro Center</div>
                                <Button
                                    variant="outlined"
                                    onClick={() => onToggleMcOfferSection(true)}
                                    disabled={loading}
                                >
                                    Attach Micro Center Offer
                                </Button>
                            </div>
                        </div>
                    )}

                    {selectedStockId && (
                        <div className={styles.details_panel}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
                                <h4 className={styles.details_panel_heading} style={{ margin: 0 }}>
                                    Retailer Offers{offers.length > 0 ? ` (${offers.length})` : ''}
                                </h4>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setRetailerOffersExpanded((previous) => !previous)}
                                    sx={{ marginLeft: 'auto' }}
                                >
                                    {retailerOffersExpanded ? 'Hide' : 'Show'}
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
                                Newegg, Walmart, and Amazon listings. The poller verifies each offer is sold
                                first-party before it appears on the site.
                            </Typography>
                            {retailerOffersExpanded && (
                                <Box sx={{ px: 2, pb: 2 }}>
                                    {offers.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            No retailer offers attached.
                                        </Typography>
                                    )}
                                    {offers.map((offer) => (
                                        <Stack
                                            key={offer.id}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1.5}
                                            sx={{ py: 0.75, borderBottom: '1px solid rgba(128,128,128,0.15)' }}
                                        >
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, minWidth: 76 }}>
                                                {RETAILER_OFFER_OPTIONS.find((option) => option.value === offer.retailer)?.label
                                                    || offer.retailer}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                {offer.externalItemNumber}
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {renderRetailerOfferVerification(offer)}
                                            </Box>
                                            <IconButton
                                                aria-label={`Remove ${offer.retailer} offer`}
                                                onClick={() => onDeleteRetailerOffer(offer)}
                                                disabled={loading}
                                                size="small"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="flex-start"
                                        flexWrap="wrap"
                                        sx={{ mt: 1.5 }}
                                    >
                                        <TextField
                                            label="Retailer"
                                            value={retailerOfferDraft.retailer}
                                            onChange={handleRetailerOfferDraftChange('retailer')}
                                            variant="standard"
                                            select
                                            sx={{ width: 110 }}
                                        >
                                            {RETAILER_OFFER_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            label="Item number"
                                            value={retailerOfferDraft.externalItemNumber}
                                            onChange={handleRetailerOfferDraftChange('externalItemNumber')}
                                            variant="standard"
                                            error={!!retailerOfferError}
                                            helperText={retailerOfferError || selectedRetailerOption?.helperText}
                                            sx={{ minWidth: 220, flex: 1 }}
                                        />
                                        <TextField
                                            label="URL (optional)"
                                            value={retailerOfferDraft.url}
                                            onChange={handleRetailerOfferDraftChange('url')}
                                            variant="standard"
                                            sx={{ minWidth: 180, flex: 1 }}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleAddRetailerOffer}
                                            disabled={loading}
                                            sx={{ mt: 1.5 }}
                                        >
                                            Add Offer
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </div>
                    )}

                    <div className={styles.details_panel}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
                            <h4 className={styles.details_panel_heading} style={{ margin: 0 }}>State Availability</h4>
                            <Stack direction="row" spacing={1} sx={{ marginLeft: 'auto' }}>
                                <Button
                                    variant={mapRetailer === 'combined' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setMapRetailer('combined')}
                                >
                                    Combined
                                </Button>
                                <Button
                                    variant={mapRetailer === 'bestbuy' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setMapRetailer('bestbuy')}
                                >
                                    Best Buy
                                </Button>
                                <Button
                                    variant={mapRetailer === 'microcenter' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setMapRetailer('microcenter')}
                                    disabled={!hasMcOffer && !mcOfferExpanded}
                                >
                                    Micro Center
                                </Button>
                            </Stack>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
                            {mapRetailer === 'combined'
                                ? 'Combined view. Select Best Buy or Micro Center to edit state availability.'
                                : `Editing ${mapRetailer === 'bestbuy' ? 'Best Buy' : 'Micro Center'} state availability. Click any state on the map to toggle it.`}
                        </Typography>
                        <AdminMapChart stateData={mapData} onToggleState={handleMapToggle} />
                    </div>

                </div>
            </div>
        </Box>
    );
};

export default AdminStockEditor;
